import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Stack,
  Divider,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { alexforbesBrand } from '../brand/alexforbes';
import BrandLogo from './BrandLogo';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login, loginWithMicrosoft, loginWithGoogle } = useAuth();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) {
      handleSocialAuthCallback(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSocialAuthCallback = async (code) => {
    try {
      const response = await fetch('/api/social/microsoft-token/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });

      if (!response.ok) {
        throw new Error('Unauthorized user');
      }

      const data = await response.json();
      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.removeItem('authToken');
        if (data.role) {
          localStorage.setItem('userRole', data.role);
        }
        navigate('/');
      } else {
        throw new Error('No token received');
      }
    } catch (err) {
      console.error('Social auth error:', err);
      setError('Access denied. Please contact your administrator for access.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const success = await login(username, password);
      if (success) {
        navigate('/');
      } else {
        setError('Access denied. Please contact your administrator for access.');
      }
    } catch (err) {
      setError(
        err?.message ||
          'Access denied. Please contact your administrator for access.'
      );
    }
  };

  const handleMicrosoftLoginClick = async () => {
    try {
      const success = await loginWithMicrosoft();
      if (success) navigate('/');
    } catch (err) {
      console.error('Microsoft login failed:', err);
      setError('Microsoft login failed. Please try again.');
    }
  };

  const handleGoogleLoginClick = async () => {
    try {
      const success = await loginWithGoogle();
      if (success) navigate('/');
    } catch (err) {
      console.error('Google login failed:', err);
      setError('Google login failed. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${alexforbesBrand.colors.sidebar} 0%, ${alexforbesBrand.colors.tealDark} 50%, #0D1114 100%)`,
        px: 2,
      }}
    >
      <Container component="main" maxWidth="xs">
        <Paper
          elevation={8}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            borderRadius: 2,
          }}
        >
          <BrandLogo variant="login" style={{ maxWidth: 280, marginBottom: 24 }} />
          {error && (
            <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 3,
                height: '42px',
                backgroundColor: alexforbesBrand.colors.orange,
                fontSize: '1rem',
                '&:hover': { backgroundColor: alexforbesBrand.colors.orangeDark },
              }}
            >
              Sign in
            </Button>
          </Box>
          <Divider sx={{ width: '100%', mt: 3, mb: 3 }}>
            <Typography variant="body2" color="textSecondary">
              OR
            </Typography>
          </Divider>
          <Stack spacing={2} sx={{ width: '100%' }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleMicrosoftLoginClick}
              sx={{
                height: '42px',
                backgroundColor: alexforbesBrand.colors.teal,
                '&:hover': { backgroundColor: alexforbesBrand.colors.tealDark },
              }}
            >
              Sign in with Microsoft
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleGoogleLoginClick}
              sx={{
                height: '42px',
                borderColor: alexforbesBrand.colors.orange,
                color: alexforbesBrand.colors.orange,
                '&:hover': {
                  borderColor: alexforbesBrand.colors.orangeLight,
                  backgroundColor: 'rgba(255, 102, 0, 0.06)',
                },
              }}
            >
              Sign in with Google
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
